<template>
	<div>
		<h1>Прайс-лист</h1>

		<div class="form-section">
			<h3>{{ form.id ? 'Редактировать' : 'Добавить' }} запчасть</h3>
			<div class="form-row">
				<PrimeInputText v-model="form.name" placeholder="Наименование" />
				<PrimeInputText v-model="form.carBrand" placeholder="Марка" />
				<PrimeInputText v-model="form.quantifier" placeholder="Мера количества" />
				<PrimeButton label="Сохранить" icon="pi pi-check" @click="save" />
				<PrimeButton v-if="form.id" label="Отмена" icon="pi pi-times" severity="secondary" @click="cancelEdit" />
			</div>
		</div>

		<PrimeDataTable
			:value="spares"
			v-model:editingRows="editingSpares"
			v-model:first="spareTableFirst"
			editMode="row"
			dataKey="id"
			stripedRows
			showGridlines
			size="small"
			:paginator="true"
			:rows="20"
			:rowsPerPageOptions="[10, 20, 50, 100]"
			paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
			currentPageReportTemplate="{first} to {last} of {totalRecords}"
			@row-edit-save="onRowEditSave"
		>
			<PrimeColumn header="(#)" :style="{ width: '1%', whiteSpace: 'nowrap' }">
				<template #body="slotProps">
					{{ spareTableFirst + slotProps.index + 1 }}
				</template>
			</PrimeColumn>

			<PrimeColumn field="name" header="Наименование">
				<template #editor="{ data, field }">
					<PrimeInputText v-model="data[field]" />
				</template>
			</PrimeColumn>

			<PrimeColumn field="carBrand" header="Марка">
				<template #editor="{ data, field }">
					<PrimeInputText v-model="data[field]" />
				</template>
			</PrimeColumn>

			<PrimeColumn field="quantifier" header="Мера количества">
				<template #editor="{ data, field }">
					<PrimeInputText v-model="data[field]" />
				</template>
			</PrimeColumn>

			<PrimeColumn
				:rowEditor="true"
				:style="{ width: '1%', whiteSpace: 'nowrap' }"
				bodyStyle="text-align:center"
			/>

			<PrimeColumn :exportable="false" :style="{ width: '1%', whiteSpace: 'nowrap' }">
				<template #body="slotProps">
					<PrimeButton icon="pi pi-trash" variant="outlined" severity="danger" @click="confirmDeleteSpare(slotProps.data)" />
				</template>
			</PrimeColumn>
		</PrimeDataTable>

		<PrimeDialog v-model:visible="deleteSpareDialog" :style="{ width: '450px' }" header="Подтверждение удаления" :modal="true">
			<div class="flex items-center gap-4">
				<i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #f44336;"></i>
				<span v-if="spare">
					Вы уверены, что хотите удалить <b>{{ spare.name }}</b>?
				</span>
			</div>
			<template #footer>
				<PrimeButton label="Нет" icon="pi pi-times" text @click="deleteSpareDialog = false" severity="secondary" variant="text" />
				<PrimeButton label="Да" icon="pi pi-check" @click="deleteSpare" severity="danger" />
			</template>
		</PrimeDialog>
	</div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { api } from "../api";
import { useToast } from 'primevue/usetoast';

const spares = ref([]);
const editingSpares = ref([]);
const spareTableFirst = ref(0);
const form = ref({ id: 0, name: "", carBrand: "", quantifier: "" });
const deleteSpareDialog = ref(false);
const toast = useToast();
const spare = ref({});

// Load data
const load = () => api.get("/sparecomponents").then(r => spares.value = r.data);
onMounted(load);

// Save
const save = () => {
	if (!form.value.name) {
		toast.add({ severity: 'warn', summary: 'Проверка', detail: 'Наименование обязательно', life: 3000 });
		return;
	}

	const promise = !form.value.id 
		? api.post("/sparecomponents", form.value)
		: api.put(`/sparecomponents/${form.value.id}`, form.value);
	
	promise.then(() => {
		afterSave();
		toast.add({ severity: 'success', summary: 'Успешно', detail: 'Запчасть сохранена', life: 3000 });
	}).catch(err => {
		toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить запчасть', life: 3000 });
		console.error(err);
	});
};

const afterSave = () => {
	form.value = { id: 0, name: "", carBrand: "", quantifier: "" };
	load();
};

const cancelEdit = () => {
	form.value = { id: 0, name: "", carBrand: "", quantifier: "" };
};

// PrimeVue row edit save
const onRowEditSave = (event) => {
	const { newData } = event;
	api.put(`/sparecomponents/${newData.id}`, newData)
		.then(() => {
			load();
			toast.add({ severity: 'success', summary: 'Успешно', detail: 'Запчасть обновлена', life: 3000 });
		})
		.catch(err => {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось обновить запчасть', life: 3000 });
			console.error(err);
		});
};

const confirmDeleteSpare = (s) => {
	spare.value = s;
	deleteSpareDialog.value = true;
};

const deleteSpare = () => {
	api.delete(`/sparecomponents/${spare.value.id}`)
		.then(() => {
			load();
			deleteSpareDialog.value = false;
			spare.value = {};
			toast.add({ severity: 'success', summary: 'Успешно', detail: 'Запчасть удалена', life: 3000 });
		})
		.catch(err => {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить запчасть', life: 3000 });
			console.error(err);
		});
};
</script>

<style scoped>
.form-section {
	background: white;
	padding: 1.5rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	margin-bottom: 2rem;
}

.form-section h3 {
	margin-bottom: 1rem;
	color: #2c3e50;
}

.form-row {
	display: flex;
	gap: 0.5rem;
	align-items: center;
	flex-wrap: wrap;
}

.form-row input {
	flex: 1;
	min-width: 150px;
}
</style>

